import React, { useEffect, useRef, useState } from "react";

import * as Sentry from "@sentry/react";
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { isEmpty, trim } from "lodash/fp";
import { useSearchParams } from "react-router-dom";

import Typography from "@mui/material/Typography";

import BillingLoadFailure from "@app/analytics/account/Billing/components/BillingLoadFailure";
import StripeForm from "@app/analytics/account/Billing/components/StripeForm";
import { BillingCard } from "@app/analytics/account/Billing/components/styles";
import useBillingCustomer from "@app/analytics/account/Billing/hooks/useBillingCustomer";
import usePaymentMethod from "@app/analytics/account/Billing/hooks/usePaymentMethod";
import useSetupIntent from "@app/analytics/account/Billing/hooks/useSetupIntent";
import * as Events from "@app/analytics/tracking/events";
import { toast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import type { PaymentMethod } from "@app/common/types";
import { withQueryParamsUrl } from "@app/common/utils";
import Config from "@app/config";
import usePageTitle from "@app/utils/hooks/usePageTitle";
import { getAppUrl } from "@app/utils/utils";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import { Link } from "@rui/Link";
import LoadSpinner from "@rui/LoadSpinner";
import Stack, { Row } from "@rui/Stack";

import StripeFormField from "./components/StripeFormField";
import * as BillingUtils from "./utils";

const DEFAULT_PAYMENT_METHOD_FAILURE_ERROR =
  "Oops! Something went wrong and your payment method could not be updated. Please refresh the page or try again later.";

const BillingUpdatePaymentMethod = ({
  hasPaymentMethod,
  initialValues,
}: {
  hasPaymentMethod: boolean;
  initialValues?: PaymentMethod["billingDetails"];
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [queryParams] = useSearchParams();

  const [isSaving, setIsSaving] = useState(false);
  const { billingCustomer, updateCustomer: updateBillingCustomer } =
    useBillingCustomer();
  const companyRef = useRef<HTMLInputElement | null>(null);

  const returnTo = queryParams.get("returnTo");

  const handleSubmit = async () => {
    if (!stripe || !elements) return;

    setIsSaving(true);
    Events.updateBillingInfo();

    try {
      if (billingCustomer && companyRef.current) {
        const company = trim(companyRef.current.value);
        await updateBillingCustomer.mutateAsync({
          id: billingCustomer.id,
          company: isEmpty(company) ? undefined : company,
        });
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: withQueryParamsUrl(Config.stripe.setupReturnUrl, {
          redirect_to: returnTo
            ? getAppUrl(returnTo, { app: "main" })
            : withQueryParamsUrl(getAppUrl(ROUTES.BILLING, { app: "main" }), {
                from: "update",
              }),
        }),
      },
    });

    setIsSaving(false);

    if (error) {
      if (error.type !== "validation_error") {
        Sentry.captureMessage(
          error.message ?? "Error updating payment method",
          { extra: { ...error } }
        );
      }
      toast.error(error.message ?? DEFAULT_PAYMENT_METHOD_FAILURE_ERROR);
    }
  };

  return (
    <BillingCard sx={{ p: 2, width: "100%", maxWidth: "500px" }}>
      <Stack spacing={2}>
        {billingCustomer && (
          <StripeFormField
            label="Company (optional)"
            name="company"
            placeholder="Company Name"
            inputProps={{ maxLength: 30 }}
            defaultValue={billingCustomer.company}
            ref={companyRef}
          />
        )}
        <AddressElement
          options={{ mode: "billing", defaultValues: initialValues }}
          onLoadError={BillingUtils.handleLoadError}
        />
        <PaymentElement onLoadError={BillingUtils.handleLoadError} />
        <Button loading={isSaving} onClick={handleSubmit}>
          {hasPaymentMethod ? "Update" : "Add"} Payment Method
        </Button>
      </Stack>
    </BillingCard>
  );
};

const BillingUpdatePaymentMethodWrapper = () => {
  usePageTitle("Billing - Update");

  const { createSetupIntent } = useSetupIntent();
  const { paymentMethod, hasPaymentMethod } = usePaymentMethod();

  useEffect(() => {
    // generate a setup intent on page load to pass to the stripe elements
    createSetupIntent.mutate({});

    if (window.READ_STRIPE_ERROR_PROMISE) {
      window.READ_STRIPE_ERROR_PROMISE.then(() =>
        setStripeLoadError(false)
      ).catch(() => {
        Sentry.captureMessage(
          "Stripe Error: Load failed during billing update"
        );
        setStripeLoadError(true);
      });
    }
  }, []);
  const [stripeLoadError, setStripeLoadError] = useState(false);

  const { isPending, isError, data } = createSetupIntent;

  if (stripeLoadError) {
    return (
      <Stack
        spacing={2}
        flex={1}
        sx={{ p: 3 }}
        variant="center"
        justifyContent="flex-start"
      >
        <Row variant="center">
          <Typography variant="h2" sx={{ fontWeight: 500 }}>
            {hasPaymentMethod ? "Update" : "Add"} Payment Method
          </Typography>
        </Row>
        <Stack sx={{ minHeight: "300px", maxWidth: "600px" }}>
          <Alert severity="error" embedded>
            Sorry, there was an error and a payment method cannot be added or
            updated. Please refresh the page or contact{" "}
            <Link href="mailto:support@read.ai">support@read.ai</Link>
          </Alert>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} flex={1} sx={{ p: 3 }}>
      <Row variant="center">
        <Typography variant="h2" sx={{ fontWeight: 500 }}>
          {hasPaymentMethod ? "Update" : "Add"} Payment Method
        </Typography>
      </Row>
      <Stack variant="center" sx={{ pt: 2, minHeight: "300px" }}>
        {isPending ? (
          <LoadSpinner />
        ) : isError ? (
          <BillingLoadFailure />
        ) : (
          <StripeForm
            options={{ clientSecret: data?.clientSecret }}
            requireClientSecret
          >
            <BillingUpdatePaymentMethod
              hasPaymentMethod={hasPaymentMethod}
              initialValues={paymentMethod?.billingDetails}
            />
          </StripeForm>
        )}
      </Stack>
    </Stack>
  );
};

export default BillingUpdatePaymentMethodWrapper;
